<template>
  <div ref="root">
    <v-expansion-panels
      flat
      v-if="rootWidth"
      v-model="panel"
      class="analysisCard pa-1 pr-3"
    >
      <v-expansion-panel class="rounded-lg">
        <v-expansion-panel-header class="px-3 py-5 align-start" disable-icon-rotate>
          <v-progress-circular
            v-if="loading"
            indeterminate
            color="#f0f0f0"
          />
          <v-row
            v-else
            no-gutters
            class="analysisInfo flex-nowrap"
            style="width: 100%;"
          >
            <v-img
              class="mr-3 rounded-lg"
              :src="leadImageUrl"
              width="58px"
              max-width="58px"
              height="58px"
              max-height="58px"
            />
            <div
              class="documentDetails"
            >
              <a :href="getRedirectUrl()" target="_blank" onclick="event.stopPropagation()">
                <h4>{{ analysisData.documentDetails.title }}</h4>
                <v-avatar v-if="showImage" size="18">
                  <v-img :src="favicon" @error="onErrorImg()" />
                </v-avatar>
              </a>
              <a :href="analysisData.documentDetails.url" target="_blank" onclick="event.stopPropagation()">
                {{ documentURL.host }}
              </a>
              <TagsListHorizontal :tags="analysisData.tags" class="pt-2" />
            </div>
          </v-row>
          <template v-slot:actions style="height: 100%;">
            <div class="d-flex flex-column">
              <v-hover v-slot="{ hover }" v-if="!reflectionReadOnly">
                <v-btn
                  icon
                  @click.stop="unlinkDocument(analysis.id)"
                  class="expansion-header-icons mt-n1"
                  :class="hover ? 'redButton' : ''"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-hover>

              <v-menu offset-y v-if="!reflectionReadOnly">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    class="expansion-header-icons py-2"
                  >
                    <v-icon>add</v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <v-list dense class="d-flex flex-column pa-0">
                    <v-list-item class="pa-0">
                      <v-btn text class="flex-grow-1 docInsertButton" @click="insertSupportingDoc">
                        {{ $t('reflectionForm.analysisCard.insertDoc.support') }}
                      </v-btn>
                    </v-list-item>
                    <v-list-item class="pa-0">
                      <v-btn text class="flex-grow-1 docInsertButton" @click="insertDocument">
                        {{ $t('reflectionForm.analysisCard.insertDoc.quote') }}
                      </v-btn>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
              <v-btn
                icon
                class="expansion-header-icons rotate"
              >
                <v-icon>expand_more</v-icon>
              </v-btn>
            </div>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="expansionElement">
          <UserAnalysisElement
            v-for="element in elements"
            :element="element"
            :key="element.text"
            @insert-block="insertBlock"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { types } from '@/store/rootTypes';
import * as analysisConstants from '@/store/analysisConstants';
import TagsListHorizontal from '@/components/tags/TagsListHorizontal';
import { fetchImagesForDocument } from '@/shared/store/documentImageHelper';
import UserAnalysisElement from '../UserAnalysisElement/UserAnalysisElement';

export default {
  name: 'ReflectionAnalysisCard',
  components: {
    UserAnalysisElement,
    TagsListHorizontal,
  },
  data: () => ({
    loading: true,
    unSubscribers: [],
    isOpen: false,
    usersAnalysisData: [],
    panel: undefined,
    showImage: true,
    rootWidth: undefined,
    leadImageUrl: '',
  }),
  props: {
    analysis: {
      type: Object,
      required: true,
    },
    reflectionId: {
      type: String,
      required: true,
    },
  },
  async beforeMount() {
    this.unSubscribers.push(await this.getAnalysis({ analysisId: this.analysis.id, onSnapshotCallback: this.setAnalysisData }));
  },
  beforeDestroy() {
    this.unSubscribers.forEach((unSub) => unSub());
  },
  watch: {
    async panel() {
      if (this.panel !== undefined) {
        this.usersElementsUnsubscriber = await this.getUsersAnalysis({
          analysisId: this.analysis.id,
          onSnapshotCallback: this.setUsersAnalysisData,
        });
      } else {
        this.usersElementsUnsubscriber();
      }
    },
  },
  methods: {
    ...mapActions({
      getAnalysis: types.analyses.actions.getAnalysis,
      getUsersAnalysis: types.analyses.actions.getUsersAnalysis,
      getUserProfile: types.users.actions.getUserProfile,
      removeReflectionAnalysis: types.reflections.actions.removeReflectionAnalysis,
      removeReflectionCategoryAnalysis: types.reflections.actions.removeReflectionCategoryAnalysis,
    }),
    onErrorImg() {
      this.showImage = false;
    },
    tagListWidth() {
      return this.rootWidth - 168;
    },
    insertBlock(blockData) {
      blockData.data.leadImageUrl = this.leadImageUrl;
      this.$emit('insert-block', blockData);
    },
    insertSupportingDoc() {
      this.$emit('insert-supporting-doc', this.analysis.id);
    },
    async unlinkDocument(analysisId) {
      const document = {
        id: analysisId,
      };
      const { reflectionId } = this;
      this.loading = true;
      await this.removeReflectionAnalysis({ document, reflectionId });
      await this.removeReflectionCategoryAnalysis({ document, ids: ['general'], reflectionId });
    },
    async insertDocument() {
      let user;
      try {
        user = this.analysisData.createdBy
          ? await this.getUserProfile(this.analysisData.createdBy)
          : undefined;
      } catch (e) {
        console.warn('createdBy NOT found or unexisting user');
        console.error(e);
      }

      const username = user
        ? `${user.firstName} ${user.lastName}`
        : '';

      const { documentDetails } = this.analysisData;
      const text = documentDetails.description || documentDetails.excerpt;

      const blockData = {
        type: analysisConstants.ANALYSIS_DOCUMENT,
        data: {
          username,
          text,
          userId: user ? user.id : undefined,
          title: documentDetails.title,
          url: documentDetails.url,
          leadImageUrl: this.leadImageUrl,
          isUpload: this.analysisData.documentDetails.isUpload,
          redirectUrl: this.getRedirectUrl(),
        },
      };
      this.$emit('insert-block', {
        ...blockData,
        analysisId: this.analysis.id,
      });
    },
    async setAnalysisData(data) {
      this.loading = false;
      this.analysisData = data.data();

      if (this.analysisData.documentDetails.isUpload) {
        // TODO I feel this should be done in the store but don't have
        // the time for this refactoring right now
        const { leadImageUrl } = await fetchImagesForDocument(this.analysisData.documentDetails);
        this.leadImageUrl = leadImageUrl;
        return;
      }
      this.leadImageUrl = this.analysisData.documentDetails.leadImageUrl;
    },
    getRedirectUrl() {
      const { isUpload, documentId, url } = this.analysisData.documentDetails;

      if (isUpload) return `/doc/${documentId}/${this.analysis.id}`;

      const getUrl = window.location;
      const baseUrl = `${getUrl.protocol}//${getUrl.host}/redirect`;
      const targetUrl = new URL(baseUrl);
      targetUrl.searchParams.append('redirectUrl', url);
      targetUrl.searchParams.append('analysisId', this.analysis.id);
      return targetUrl.toString();
    },
    async setUsersAnalysisData(data) {
      this.usersAnalysisData = [];
      // TODO find how wait for items to be loaded ... is this working well? not sure yet, need to do more testing
      const docsdata = await data.docs;
      docsdata.forEach(async doc => {
        const docData = await doc.data();
        const user = await this.getUserProfile(docData.id);

        const { significantElements } = docData.evaluation;
        significantElements.forEach(item => this.usersAnalysisData.push({
          ...item,
          element: true,
          analysisId: this.analysis.id,
          userId: docData.id,
          username: `${user.firstName} ${user.lastName}`,
          leadImageUrl: this.analysisData.documentDetails.leadImageUrl,
          title: this.analysisData.documentDetails.title,
          url: this.analysisData.documentDetails.url,
          isUpload: this.analysisData.documentDetails.isUpload,
          redirectUrl: this.getRedirectUrl(),
        }));

        const reflectionComments = docData.reflection.comments;
        reflectionComments.forEach(item => this.usersAnalysisData.push({
          ...item,
          comment: true,
          analysisId: this.analysis.id,
          userId: docData.id,
          username: `${user.firstName} ${user.lastName}`,
          leadImageUrl: this.analysisData.documentDetails.leadImageUrl,
          title: this.analysisData.documentDetails.title,
          url: this.analysisData.documentDetails.url,
          isUpload: this.analysisData.documentDetails.isUpload,
          redirectUrl: this.getRedirectUrl(),
        }));
      });
    },
  },
  computed: {
    ...mapGetters({
      reflectionReadOnly: types.reflections.getters.reflectionReadOnly,
    }),
    documentURL() {
      if (this.analysisData.documentDetails.url) return new URL(this.analysisData.documentDetails.url);
      return '';
    },
    favicon() {
      return `${this.documentURL.origin}/favicon.ico`;
    },
    elements() {
      // use a map reduce on this.usersAnalysisData to get a list of comments + significantElements to feed userAnalysisElements
      return this.usersAnalysisData;
    },
  },
  mounted() {
    this.rootWidth = this.$refs.root.clientWidth;
  },
};
/* eslint-disable max-len */
</script>

<style scoped>
.docInsertButton {
  text-transform: none;
  font-size: 13px;
  justify-content: flex-start;
}

::v-deep .v-expansion-panel--active > .v-expansion-panel-header--active > .v-expansion-panel-header__icon > div > .expansion-header-icons.rotate {
  transform: rotate(-180deg);
}

.expansion-header-icons {
  font-size: 24px;
}

.expansion-header-icons.v-btn.v-btn--icon.redButton .v-btn__content .v-icon {
  color: red;
}

.v-progress-circular {
  display: block;
  margin: auto;
}

.documentDetails {
  flex-grow: 1;
  font-size: 13px;
  line-height: 15px;
}

.documentDetails h4 {
  font-weight: 600;
  margin-bottom: 8px;
  text-decoration: none;
  color: black;
}
.documentDetails a {
  text-decoration: none;
  color: #3F47CD;
  font-weight: 500;
}
</style>
<style>
.ps__rail-y {
  z-index: 1;
}
.ps__rail-x {
  z-index: 1;
}
</style>
